import React from 'react';
import Button from './Button';
import Card from './Card';

/**
 * Form component props interface
 */
export interface FormProps extends React.FormHTMLAttributes<HTMLFormElement> {
  /** Form title */
  title?: string;
  /** Form description */
  description?: string;
  /** Form content */
  children: React.ReactNode;
  /** Whether to wrap form in a card */
  card?: boolean;
  /** Loading state for form submission */
  isLoading?: boolean;
  /** Submit button text */
  submitText?: string;
  /** Cancel button text */
  cancelText?: string;
  /** Function to call when cancel is clicked */
  onCancel?: () => void;
  /** Whether to show form actions (submit/cancel buttons) */
  showActions?: boolean;
  /** Additional CSS class names */
  className?: string;
  /** Custom inline styles */
  style?: React.CSSProperties;
}

/**
 * Form component following design system standards
 * Composite component that combines Card, Button, and other UI elements
 * 
 * @example
 * ```tsx
 * <Form 
 *   title="Login" 
 *   description="Enter your credentials"
 *   onSubmit={handleSubmit}
 *   submitText="Sign In"
 *   card
 * >
 *   <Input label="Email" type="email" required />
 *   <Input label="Password" type="password" required />
 * </Form>
 * ```
 */
const Form: React.FC<FormProps> = ({
  title,
  description,
  children,
  card = false,
  isLoading = false,
  submitText = 'Submit',
  cancelText = 'Cancel',
  onCancel,
  showActions = true,
  className,
  style,
  ...props
}) => {
  const getFormStyles = (): React.CSSProperties => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    ...style,
  });

  const getHeaderStyles = (): React.CSSProperties => ({
    marginBottom: '8px',
  });

  const getTitleStyles = (): React.CSSProperties => ({
    fontSize: '24px',
    fontWeight: 600,
    color: '#1F2937',
    margin: '0 0 8px 0',
  });

  const getDescriptionStyles = (): React.CSSProperties => ({
    fontSize: '14px',
    color: '#6B7280',
    margin: 0,
    lineHeight: '1.5',
  });

  const getActionsStyles = (): React.CSSProperties => ({
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '8px',
  });

  const formContent = (
    <form
      style={getFormStyles()}
      className={className}
      {...props}
    >
      {(title || description) && (
        <div style={getHeaderStyles()}>
          {title && <h2 style={getTitleStyles()}>{title}</h2>}
          {description && <p style={getDescriptionStyles()}>{description}</p>}
        </div>
      )}
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {children}
      </div>
      
      {showActions && (
        <div style={getActionsStyles()}>
          {onCancel && (
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={isLoading}
            >
              {cancelText}
            </Button>
          )}
          <Button
            type="submit"
            variant="primary"
            isLoading={isLoading}
            disabled={isLoading}
          >
            {submitText}
          </Button>
        </div>
      )}
    </form>
  );

  if (card) {
    return (
      <Card variant="elevated" padding="lg">
        {formContent}
      </Card>
    );
  }

  return formContent;
};

Form.displayName = 'Form';

export default Form;